const pendingIframes = new WeakSet<HTMLIFrameElement>();

type VimeoPlayerMessage = { event?: string; method?: string; value?: number };

/**
 * Correct Vimeo's sometimes incorrect oEmbed dimensions using the loaded player's native size.
 * Uses the same message protocol as Vimeo's Player SDK without loading another script.
 *
 * @param {HTMLIFrameElement} iframe Vimeo player iframe.
 *
 * @returns {void}
 */
export const syncVimeoAspectRatio = (iframe: HTMLIFrameElement): void => {
  const playerWindow = iframe.contentWindow;
  const hostWindow = iframe.ownerDocument.defaultView;

  if (!playerWindow || !hostWindow || pendingIframes.has(iframe)) {
    return;
  }

  // FitVids also sizes deferred embeds before their source is loaded.
  const playerSrc = iframe.getAttribute('src') || iframe.getAttribute('data-src');

  if (!playerSrc) {
    return;
  }

  const { origin } = new URL(playerSrc, iframe.ownerDocument.baseURI);

  if ('https://player.vimeo.com' !== origin && 'http://player.vimeo.com' !== origin) {
    return;
  }

  pendingIframes.add(iframe);

  let width = 0;
  let height = 0;
  let timeout = 0;

  const requestDimensions = (): void => {
    // Inserting a deferred iframe can emit an initial about:blank load.
    if (iframe.hasAttribute('data-src') || !iframe.getAttribute('src')) {
      return;
    }

    if (!timeout) {
      timeout = hostWindow.setTimeout(onTimeout, 30000);
    }

    playerWindow.postMessage({ method: 'getVideoWidth' }, origin);
    playerWindow.postMessage({ method: 'getVideoHeight' }, origin);
  };

  const cleanup = (listener: (event: MessageEvent<VimeoPlayerMessage | string>) => void): void => {
    hostWindow.removeEventListener('message', listener);
    iframe.removeEventListener('load', requestDimensions);
    hostWindow.clearTimeout(timeout);
    pendingIframes.delete(iframe);
  };

  const onMessage = (event: MessageEvent<VimeoPlayerMessage | string>): void => {
    if (event.source !== playerWindow || event.origin !== origin) {
      return;
    }

    let data: VimeoPlayerMessage;

    try {
      data = 'string' === typeof event.data ? (JSON.parse(event.data) as VimeoPlayerMessage) : event.data;
    } catch {
      return;
    }

    if (!data) {
      return;
    }

    if ('ready' === data.event || 'ping' === data.method) {
      requestDimensions();
      return;
    }

    if ('number' !== typeof data.value || !Number.isFinite(data.value) || 0 >= data.value) {
      return;
    }

    if ('getVideoWidth' === data.method) {
      width = data.value;
    } else if ('getVideoHeight' === data.method) {
      height = data.value;
    }

    const wrapper = iframe.parentElement;

    if (width && height && wrapper?.classList.contains('fluid-width-video-wrapper')) {
      wrapper.style.paddingTop = `${(height / width) * 100}%`;
      // Preserve the corrected ratio if the builder unwraps and initializes this iframe again.
      iframe.setAttribute('data-et-oembed-width', String(width));
      iframe.setAttribute('data-et-oembed-height', String(height));
      cleanup(onMessage);
    }
  };

  /**
   * Stop waiting when a loaded player does not report its dimensions.
   */
  function onTimeout(): void {
    cleanup(onMessage);
  }

  hostWindow.addEventListener('message', onMessage);
  iframe.addEventListener('load', requestDimensions);
  // Deferred embeds can stay offscreen longer than the response timeout. Begin the
  // timeout only when the player has a source or emits its load/ready event.
  if (!iframe.hasAttribute('data-src') && iframe.getAttribute('src')) {
    timeout = hostWindow.setTimeout(onTimeout, 30000);
    playerWindow.postMessage({ method: 'ping' }, origin);
  }
};
